/**
 * SIP Traffic Logs Page
 * 
 * Real-time SIP protocol traffic monitoring with detailed message logging.
 * Uses common components for consistent UI patterns.
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { SipTrafficEvent } from '../../services/api';
// Type-only import - safe for browser (types are erased at runtime)
import type { Logging } from '@sbclite/core';
import type { SipPeerInfo, SipPeersResponse } from '../../types/sip';
import { useAutoRefresh, REFRESH_INTERVALS, formatInterval } from '../../hooks/useAutoRefresh';
import toast from 'react-hot-toast';
import {
  Search,
  Filter,
  Clock,
  X,
  Download,
  Phone,
  Signal,
  RefreshCw,
  Settings,
  ChevronDown,
  Check,
} from 'lucide-react';
import {
  EmptyState,
} from '../../components/common';
import { SipLadderDiagram } from '../../components/sip/SipLadderDiagram';
import { useAuth } from '../../contexts/AuthContext';
import { SIP_TRACE_PAGE_SIZE, csvRows, formatTrafficTimestamp, headerEntries, relatedCallIdsParam, traceScopeKey, uniqueCallIds } from '../../lib/sip-traffic';

// SIP Traffic Message type (from call events, not log entries)
// Use core types for settings
type SipTrafficSettings = Logging.SipTrafficSettingsInfo;

interface TrafficFilters {
  callId?: string;
  relatedCallIds?: string;
  containerId?: string;
  sipPeerId?: string;
  // Comma-separated list (e.g. "UDP,TLS")
  transport?: string;
  // Comma-separated list (e.g. "INVITE,ACK")
  sipMethod?: string;
  direction?: 'inbound' | 'outbound';
  startTime?: string;
  endTime?: string;
}

// Stable, module-level option lists (referenced in useMemo deps).
const transportOptions = ['UDP', 'TCP', 'TLS', 'WS', 'WSS'] as const;
type TransportOption = (typeof transportOptions)[number];

const methodOptions = [
  'INVITE',
  'ACK',
  'BYE',
  'CANCEL',
  'REGISTER',
  'OPTIONS',
  'INFO',
  'PRACK',
  'UPDATE',
  'REFER',
  'SUBSCRIBE',
  'NOTIFY',
  'MESSAGE',
  'PUBLISH',
] as const;
type MethodOption = (typeof methodOptions)[number];

export function SipTrafficLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [filters, setFilters] = useState<TrafficFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selectedLog, setSelectedLog] = useState<SipTrafficEvent | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [tracePage, setTracePage] = useState(1);
  const [traceEndTime, setTraceEndTime] = useState<string | undefined>();
  const [selectedTraceUrlKey, setSelectedTraceUrlKey] = useState<string | undefined>();
  const [sipPeerSearch, setSipPeerSearch] = useState('');
  const [showSipPeerDropdown, setShowSipPeerDropdown] = useState(false);
  const [showTransportDropdown, setShowTransportDropdown] = useState(false);
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const next: TrafficFilters = {};
    for (const key of ['callId', 'relatedCallIds', 'containerId', 'sipPeerId', 'transport', 'sipMethod', 'direction', 'startTime', 'endTime'] as const) {
      const value = searchParams.get(key)?.trim();
      if (value) Object.assign(next, { [key]: value });
    }
    setFilters(next);
    setPage(Number(searchParams.get('page')) || 1);
  }, [searchParams]);

  const updateUrl = (next: TrafficFilters, nextPage = 1) => {
    const params = new URLSearchParams();
    Object.entries(next).forEach(([key, value]) => { if (value) params.set(key, value); });
    if (nextPage > 1) params.set('page', String(nextPage));
    setSearchParams(params);
  };


  const selectedTransports = useMemo<TransportOption[]>(() => {
    const raw = (filters.transport || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const allowed = new Set<string>(transportOptions);
    return raw.filter((t) => allowed.has(t)) as TransportOption[];
  }, [filters.transport]);

  const selectedTransportSet = useMemo(() => {
    return new Set<TransportOption>(selectedTransports);
  }, [selectedTransports]);

  const transportLabel = useMemo(() => {
    if (selectedTransports.length === 0) return 'All transports';
    return selectedTransports.join(', ');
  }, [selectedTransports]);

  const selectedMethods = useMemo<MethodOption[]>(() => {
    const raw = (filters.sipMethod || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const allowed = new Set<string>(methodOptions);
    return raw.filter((m) => allowed.has(m)) as MethodOption[];
  }, [filters.sipMethod]);

  const selectedMethodSet = useMemo(() => {
    return new Set<MethodOption>(selectedMethods);
  }, [selectedMethods]);

  const methodLabel = useMemo(() => {
    if (selectedMethods.length === 0) return 'All methods';
    return selectedMethods.join(', ');
  }, [selectedMethods]);

  const activeFilterCount = Object.values(filters).filter(v => v).length;
  const { data: sipPeersResponse } = useQuery<SipPeersResponse>({
    queryKey: ['sip-peers-cache'],
    queryFn: async () => {
      const response = await api.get<SipPeersResponse>('/api/sip-peers', { limit: 1000, offset: 0 });
      return response.data as SipPeersResponse;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    enabled: isAdmin,
  });
  const sipPeerNameMap = useMemo<Map<string, string>>(() => {
    const peers = sipPeersResponse?.data || [];
    return new Map<string, string>(peers.map((peer: SipPeerInfo) => [peer.id, peer.name]));
  }, [sipPeersResponse]);

  const sipPeersSorted = useMemo<SipPeerInfo[]>(() => {
    const peers = sipPeersResponse?.data || [];
    return [...peers].sort((a, b) => a.name.localeCompare(b.name));
  }, [sipPeersResponse]);

  const selectedSipPeerName = useMemo(() => {
    if (!filters.sipPeerId) return '';
    return sipPeerNameMap.get(filters.sipPeerId) || '';
  }, [filters.sipPeerId, sipPeerNameMap]);

  const filteredSipPeers = useMemo(() => {
    const q = sipPeerSearch.trim().toLowerCase();
    if (!q) return sipPeersSorted;
    return sipPeersSorted.filter((p) => p.name.toLowerCase().includes(q));
  }, [sipPeerSearch, sipPeersSorted]);

  // Keep the search input in sync with the currently-selected filter.
  useEffect(() => {
    if (showSipPeerDropdown) return;
    if (!filters.sipPeerId) {
      if (sipPeerSearch !== '') setSipPeerSearch('');
      return;
    }
    if (selectedSipPeerName && sipPeerSearch !== selectedSipPeerName) {
      setSipPeerSearch(selectedSipPeerName);
    }
  }, [filters.sipPeerId, selectedSipPeerName, showSipPeerDropdown, sipPeerSearch]);

  // Session-persisted auto-refresh
  const autoRefresh = useAutoRefresh({
    key: 'sip_traffic_autorefresh',
    defaultEnabled: false,
    defaultInterval: 5000,
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showSettings && !target.closest('.settings-container')) {
        setShowSettings(false);
      }
      if (showExportMenu && !target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
      if (showSipPeerDropdown && !target.closest('.sip-peer-combobox-container')) {
        setShowSipPeerDropdown(false);
      }
      if (showTransportDropdown && !target.closest('.transport-dropdown-container')) {
        setShowTransportDropdown(false);
      }
      if (showMethodDropdown && !target.closest('.method-dropdown-container')) {
        setShowMethodDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings, showExportMenu, showSipPeerDropdown, showTransportDropdown, showMethodDropdown]);

  // Fetch SIP traffic logging settings
  const { data: settingsData, isLoading: settingsLoading, isError: settingsError } = useQuery({
    queryKey: ['sip-traffic-settings'],
    queryFn: async () => {
      const response = await api.getSipTrafficSettings();
      return response;
    },
    refetchInterval: autoRefresh.enabled ? 10000 : false,
    enabled: isAdmin,
  });

  // Fetch SIP traffic messages
  const {
    data: logsData,
    isLoading: logsLoading,
    isFetching: isFetchingLogs,
    isError: logsError,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ['sip-traffic-messages', { filters, page, pageSize }],
    queryFn: async () => {
      const response = await api.getSipTrafficMessages({ 
        page, 
        pageSize, 
        ...filters,
        order: 'desc',
      });
      return response;
    },
    refetchInterval: autoRefresh.enabled && page === 1 ? autoRefresh.interval : false,
    enabled: isAdmin,
  });

  // Fetch call flow events when a log is selected (all events with same Call-ID)
  // For B2BUA calls, this queries both inbound and outbound legs
  const traceCallIds = useMemo(() => uniqueCallIds([
    selectedLog?.callId || '',
    ...(filters.callId ? filters.callId.split(',') : []),
    ...(filters.relatedCallIds ? filters.relatedCallIds.split(',') : []),
  ]), [selectedLog?.callId, filters.callId, filters.relatedCallIds]);
  const traceUrlKey = traceScopeKey(filters.callId, filters.relatedCallIds);
  useEffect(() => {
    setSelectedLog(null);
    setSelectedTraceUrlKey(undefined);
    setTracePage(1);
    setTraceEndTime(undefined);
  }, [traceUrlKey]);
  const { data: callFlowData, isLoading: callFlowLoading, isError: callFlowError } = useQuery({
    queryKey: ['sip-call-flow', traceCallIds, tracePage, traceEndTime],
    queryFn: async () => {
      if (traceCallIds.length === 0) return null;
      const response = await api.getSipTrafficMessages({
        page: tracePage,
        pageSize: SIP_TRACE_PAGE_SIZE,
        relatedCallIds: relatedCallIdsParam(traceCallIds),
        order: 'asc',
        ...(traceEndTime ? { endTime: traceEndTime } : {}),
      });
      if (!traceEndTime) setTraceEndTime(response.traceEndTime);
      return response;
    },
    enabled: isAdmin && traceCallIds.length > 0 && selectedTraceUrlKey === traceUrlKey,
  });

  // Sort call flow events by timestamp
  const callFlowEvents = useMemo(() => {
    if (!callFlowData?.data) return [];
    return [...callFlowData.data].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [callFlowData]);

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: Partial<Pick<SipTrafficSettings, 'enabled' | 'logLevel' | 'logRawMessages' | 'logBodies' | 'forceFullReload' | 'gracefulRestart'>>) => {
      const response = await api.updateSipTrafficSettings(updates);
      return response;
    },
    onSuccess: () => {
      toast.success('SIP traffic logging settings updated');
      queryClient.invalidateQueries({ queryKey: ['sip-traffic-settings'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to update settings: ${error.message}`);
    },
  });

  // Quick enable mutation
  const enableMutation = useMutation({
    mutationFn: async () => {
      const response = await api.enableSipTrafficLogging();
      return response;
    },
    onSuccess: () => {
      toast.success('SIP traffic logging enabled');
      queryClient.invalidateQueries({ queryKey: ['sip-traffic-settings'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to enable: ${error.message}`);
    },
  });

  // Quick disable mutation
  const disableMutation = useMutation({
    mutationFn: async () => {
      const response = await api.disableSipTrafficLogging();
      return response;
    },
    onSuccess: () => {
      toast.success('SIP traffic logging disabled');
      queryClient.invalidateQueries({ queryKey: ['sip-traffic-settings'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to disable: ${error.message}`);
    },
  });

  const logs: SipTrafficEvent[] = logsData?.data || [];
  const displayedLogs = logs;
  const logsMeta = { 
    total: logsData?.total || 0, 
    page: logsData?.page || 1, 
    pageSize: logsData?.pageSize || 50, 
    totalPages: logsData?.totalPages || 1
  };

  const settings: SipTrafficSettings = settingsData || {
    id: 'global',
    enabled: false,
    logLevel: 'info',
    logRawMessages: false,
    logBodies: false,
    forceFullReload: false,
    gracefulRestart: true,
    registeredDeliveryRolloutPolicy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const handleFilterChange = (key: keyof TrafficFilters, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (value) {
        (newFilters as Record<string, string>)[key] = value;
      } else {
        delete newFilters[key];
      }
      updateUrl(newFilters, 1);
      return newFilters;
    });
    setPage(1);
  };

  const toggleTransport = (transport: TransportOption) => {
    const next = new Set<TransportOption>(selectedTransportSet);
    if (next.has(transport)) next.delete(transport);
    else next.add(transport);

    const ordered = transportOptions.filter((t) => next.has(t));

    // Treat "none" and "all" as no filter.
    if (ordered.length === 0 || ordered.length === transportOptions.length) {
      handleFilterChange('transport', '');
      return;
    }

    handleFilterChange('transport', ordered.join(','));
  };

  const toggleMethod = (method: MethodOption) => {
    const next = new Set<MethodOption>(selectedMethodSet);
    if (next.has(method)) next.delete(method);
    else next.add(method);

    const ordered = methodOptions.filter((m) => next.has(m));

    // Treat "none" and "all" as no filter.
    if (ordered.length === 0 || ordered.length === methodOptions.length) {
      handleFilterChange('sipMethod', '');
      return;
    }

    handleFilterChange('sipMethod', ordered.join(','));
  };

  const handleSelectSipPeer = (peerId: string) => {
    if (!peerId) {
      handleFilterChange('sipPeerId', '');
      setSipPeerSearch('');
      setShowSipPeerDropdown(false);
      return;
    }
    const name = sipPeerNameMap.get(peerId) || '';
    handleFilterChange('sipPeerId', peerId);
    if (name) setSipPeerSearch(name);
    setShowSipPeerDropdown(false);
  };

  const handleDownloadCallFlow = () => {
    if (!selectedLog || callFlowEvents.length === 0) return;

    const content = callFlowEvents
      .map((event) => {
        const header = `${event.timestamp} ${event.direction.toUpperCase()} ${event.sipMethod || event.responseCode || ''} ${event.reasonPhrase || ''}`.trim();
        const raw = event.rawMessage || 'RAW MESSAGE NOT LOGGED';
        return `${header}
${raw}`;
      })
      .join('\n\n---\n\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `sip-call-flow-${selectedLog.callId}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
    updateUrl({}, 1);
  };

  const openLog = (event: SipTrafficEvent) => {
    setSelectedLog(event);
    setSelectedTraceUrlKey(traceUrlKey);
    setTracePage(1);
    setTraceEndTime(undefined);
  };

  const formatTimestamp = formatTrafficTimestamp;

  const downloadLogsAsCSV = () => {
    const csvContent = csvRows(displayedLogs);

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sip-traffic-logs-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadLogsAsJSON = () => {
    const dataStr = JSON.stringify(displayedLogs, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `sip-traffic-logs-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (!isAdmin) {
    return (
      <div role="alert" className="rounded-lg border border-gray-200 bg-white p-8">
        <h1 className="text-2xl font-bold text-gray-900">SIP Traffic Logs</h1>
        <p className="mt-2 text-gray-600">SIP traffic visibility and logging settings are available to administrators only.</p>
      </div>
    );
  }

  return (
      <div className="space-y-6">
      {settingsError && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">Logging settings could not be loaded. No settings change was made.</div>}
      {/* Header with Status Badge */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">SIP Traffic Logs</h1>
            {settingsLoading ? (
              <div className="animate-pulse h-6 w-20 bg-gray-200 rounded"></div>
            ) : (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                settings.enabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                <span className={`w-2 h-2 rounded-full mr-1.5 ${
                  settings.enabled ? 'bg-green-400' : 'bg-gray-400'
                }`}></span>
                {settings.enabled ? 'Logging Active' : 'Logging Disabled'}
              </span>
            )}
            {settings.enabled && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {settings.logLevel.toUpperCase()}
              </span>
            )}
          </div>
          <p className="text-gray-600">Real-time SIP protocol traffic monitoring with detailed message logging</p>
          {logsData?.redacted && <p className="mt-1 text-xs text-green-700">Credential-bearing traffic fields are redacted by the server.</p>}
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetchLogs()}
            disabled={isFetchingLogs}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetchingLogs ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <div className="flex gap-2">
            {/* Settings Button */}
            <div className="relative settings-container">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </button>

              {showSettings && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">SIP Traffic Logging Settings</h3>
                  
                  {/* Enable/Disable Toggle */}
                  <div className="mb-4">
                    <label className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Enable Logging</span>
                      <button
                        onClick={() => {
                          if (settings.enabled) {
                            disableMutation.mutate();
                          } else {
                            enableMutation.mutate();
                          }
                        }}
                        disabled={enableMutation.isPending || disableMutation.isPending}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.enabled ? 'bg-green-600' : 'bg-gray-200'
                        } ${(enableMutation.isPending || disableMutation.isPending) ? 'opacity-50' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      {settings.enabled 
                        ? 'SIP messages are being logged to database' 
                        : 'No SIP traffic is being logged (zero cost)'}
                    </p>
                  </div>

                  {/* Log Level */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Log Level
                    </label>
                    <select
                      value={settings.logLevel}
                      onChange={(e) => updateSettingsMutation.mutate({ 
                        logLevel: e.target.value as 'debug' | 'info' | 'warn' 
                      })}
                      disabled={!settings.enabled || updateSettingsMutation.isPending}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
                    >
                      <option value="debug">Debug - ALL messages (OPTIONS, INFO, etc.)</option>
                      <option value="info">Info - Call messages + REGISTER + responses</option>
                      <option value="warn">Warn - Errors only (4xx-6xx) + BYE/CANCEL</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {settings.logLevel === 'debug' && 'Logs every SIP message including OPTIONS keepalives'}
                      {settings.logLevel === 'info' && 'Logs INVITE, BYE, ACK, CANCEL, PRACK, REGISTER and all responses'}
                      {settings.logLevel === 'warn' && 'Only logs error responses and call terminations'}
                    </p>
                  </div>

                  {/* Advanced Options */}
                  <div className="space-y-3 mb-4">
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Log Raw Messages</span>
                      <input
                        type="checkbox"
                        checked={settings.logRawMessages}
                        onChange={(e) => updateSettingsMutation.mutate({ 
                          logRawMessages: e.target.checked 
                        })}
                        disabled={!settings.enabled || updateSettingsMutation.isPending}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Log SDP Bodies</span>
                      <input
                        type="checkbox"
                        checked={settings.logBodies}
                        onChange={(e) => updateSettingsMutation.mutate({ 
                          logBodies: e.target.checked 
                        })}
                        disabled={!settings.enabled || updateSettingsMutation.isPending}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </label>
                  </div>

                  {/* Engine reload behavior (independent of traffic logging) */}
                  <div className="my-4 border-t border-gray-200" />
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Engine reload behavior</h3>
                  <div className="space-y-3 mb-4">
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">
                        Graceful restart
                        <span className="block text-xs text-gray-500">Drain active calls before restarting interfaces on config change</span>
                      </span>
                      <input
                        type="checkbox"
                        checked={settings.gracefulRestart}
                        onChange={(e) => updateSettingsMutation.mutate({
                          gracefulRestart: e.target.checked
                        })}
                        disabled={updateSettingsMutation.isPending}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">
                        Force full reload
                        <span className="block text-xs text-gray-500">Always reload all settings instead of granular per-entity updates</span>
                      </span>
                      <input
                        type="checkbox"
                        checked={settings.forceFullReload}
                        onChange={(e) => updateSettingsMutation.mutate({
                          forceFullReload: e.target.checked
                        })}
                        disabled={updateSettingsMutation.isPending}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </label>
                  </div>

                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Changes take effect within 5-10 seconds on all containers
                    </p>
                  </div>

                  <div className="my-4 border-t border-gray-200" />

                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Auto-refresh</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Refresh interval
                    </label>
                    <select
                      value={autoRefresh.interval}
                      onChange={(e) => autoRefresh.setInterval(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {REFRESH_INTERVALS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      When enabled, refreshes every {formatInterval(autoRefresh.interval)}.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Export Dropdown */}
            <div className="relative export-menu-container">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>

              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                  <button
                    onClick={() => {
                      downloadLogsAsCSV();
                      setShowExportMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                      Export current page as CSV
                  </button>
                  <button
                    onClick={() => {
                      downloadLogsAsJSON();
                      setShowExportMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                      Export current page as JSON
                  </button>
                </div>
              )}
            </div>

            {/* Auto-refresh Toggle with Settings */}
            <div className="relative">
              <button
                onClick={autoRefresh.toggle}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg border ${
                  autoRefresh.enabled
                    ? 'bg-green-50 text-green-700 border-green-300'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh.enabled ? 'animate-spin' : ''}`} />
                {autoRefresh.enabled ? 'Auto-refresh On' : 'Auto-refresh Off'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center text-sm font-medium text-gray-700">
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transport
                  </label>
                  <div className="relative transport-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setShowTransportDropdown((v) => !v)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:bg-gray-50"
                    >
                      <span className="text-sm text-gray-900">{transportLabel}</span>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>

                    {showTransportDropdown && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-2">
                        <div className="px-2 py-1 text-xs font-medium text-gray-500">Supported in engine</div>
                        {transportOptions.map((t) => (
                          <label
                            key={t}
                            className="flex items-center justify-between px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedTransportSet.has(t)}
                                onChange={() => toggleTransport(t)}
                                className="h-4 w-4 text-blue-600"
                              />
                              <span className="text-sm text-gray-900">{t}</span>
                            </div>
                            {selectedTransportSet.has(t) && <Check className="h-4 w-4 text-blue-600" />}
                          </label>
                        ))}

                        <div className="mt-2 border-t border-gray-100" />
                        <button
                          type="button"
                          onClick={() => handleFilterChange('transport', '')}
                          className="mt-2 w-full px-2 py-2 text-sm text-gray-700 rounded-md hover:bg-gray-50 text-left"
                        >
                          Clear transport filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Direction
                  </label>
                  <select
                    value={filters.direction || ''}
                    onChange={(e) => handleFilterChange('direction', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">All directions</option>
                    <option value="inbound">Inbound</option>
                    <option value="outbound">Outbound</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SIP Method
                  </label>
                  <div className="relative method-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setShowMethodDropdown((v) => !v)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:bg-gray-50"
                    >
                      <span className="text-sm text-gray-900">{methodLabel}</span>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>

                    {showMethodDropdown && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-2 max-h-64 overflow-auto">
                        <div className="px-2 py-1 text-xs font-medium text-gray-500">SIP methods</div>

                        {methodOptions.map((m) => (
                          <label
                            key={m}
                            className="flex items-center justify-between px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedMethodSet.has(m)}
                                onChange={() => toggleMethod(m)}
                                className="h-4 w-4 text-blue-600"
                              />
                              <span className="text-sm text-gray-900">{m}</span>
                            </div>
                            {selectedMethodSet.has(m) && <Check className="h-4 w-4 text-blue-600" />}
                          </label>
                        ))}

                        <div className="mt-2 border-t border-gray-100" />
                        <button
                          type="button"
                          onClick={() => handleFilterChange('sipMethod', '')}
                          className="mt-2 w-full px-2 py-2 text-sm text-gray-700 rounded-md hover:bg-gray-50 text-left"
                        >
                          Clear method filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SIP Peer
                  </label>
                  <div className="relative sip-peer-combobox-container">
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={sipPeerSearch}
                      onChange={(e) => {
                        const next = e.target.value;
                        // User is searching for a different peer; don't keep an old peerId filter applied.
                        if (filters.sipPeerId) handleFilterChange('sipPeerId', '');
                        setSipPeerSearch(next);
                        setShowSipPeerDropdown(true);
                      }}
                      onFocus={() => setShowSipPeerDropdown(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setShowSipPeerDropdown(false);
                          // Restore the selected peer name (if any) when canceling search.
                          if (filters.sipPeerId) setSipPeerSearch(selectedSipPeerName);
                          else setSipPeerSearch('');
                        }
                      }}
                      onBlur={() => {
                        // Allow option selection via mouse (onMouseDown) before closing.
                        window.setTimeout(() => setShowSipPeerDropdown(false), 100);
                      }}
                      placeholder="All SIP Peers"
                      className="w-full px-3 py-2 pl-10 pr-16 border border-gray-300 rounded-lg"
                    />
                    <div className="absolute right-2 top-2 flex items-center gap-1">
                      {(filters.sipPeerId || sipPeerSearch) && (
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectSipPeer('')}
                          className="p-1 rounded hover:bg-gray-100"
                          title="Clear SIP peer filter"
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                      )}
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>

                    {showSipPeerDropdown && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectSipPeer('')}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                            !filters.sipPeerId ? 'bg-gray-50' : ''
                          }`}
                        >
                          <span className="text-gray-700">All SIP Peers</span>
                          {!filters.sipPeerId && <Check className="h-4 w-4 text-blue-600" />}
                        </button>

                        <div className="border-t border-gray-100" />

                        {sipPeersSorted.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">No SIP peers found</div>
                        ) : filteredSipPeers.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
                        ) : (
                          filteredSipPeers.slice(0, 100).map((peer) => (
                            <button
                              key={peer.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleSelectSipPeer(peer.id)}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                filters.sipPeerId === peer.id ? 'bg-gray-50' : ''
                              }`}
                            >
                              <span className="text-gray-900">{peer.name}</span>
                              {filters.sipPeerId === peer.id && <Check className="h-4 w-4 text-blue-600" />}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Call-ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={filters.callId || ''}
                      onChange={(e) => handleFilterChange('callId', e.target.value)}
                      placeholder="Filter by Call-ID..."
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg"
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>
                </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        {logsError && logs.length > 0 && <div role="status" className="border-b border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Refresh failed; showing the last successful traffic page as stale.</div>}
        {logsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading SIP traffic logs...</p>
          </div>
        ) : logsError && logs.length === 0 ? (
          <div role="alert" className="p-8 text-center text-red-700">SIP traffic could not be loaded. Try refresh; existing data was not replaced with an empty result.</div>
        ) : settingsError ? (
          <div role="alert" className="p-8 text-center text-red-700">Logging status is unavailable. Traffic results are shown only if the server returned them.</div>
        ) : !settings.enabled && logs.length === 0 ? (
          <div className="p-8 text-center">
            <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">SIP Traffic Logging Disabled</h3>
            <p className="text-gray-600 mb-4">
              Enable SIP traffic logging to capture and analyze SIP protocol messages
            </p>
            <button
              onClick={() => enableMutation.mutate()}
              disabled={enableMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {enableMutation.isPending ? 'Enabling...' : 'Enable SIP Logging'}
            </button>
          </div>
        ) : displayedLogs.length === 0 ? (
          <EmptyState
            icon={Signal}
            title="No SIP Traffic Yet"
            description="Waiting for SIP messages... Traffic will appear here automatically."
            isFiltered={Object.values(filters).some(v => v)}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transport</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direction</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method/Response</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Call-ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SIP Peer</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Container</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayedLogs.map((log) => {
                        return (
                          <tr
                            key={log.id}
                            onClick={() => openLog(log)}
                            className="hover:bg-gray-50 cursor-pointer"
                          >
                            <td className="px-4 py-3 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 mr-2 text-gray-400" />
                                {formatTimestamp(log.timestamp)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                log.transport === 'TLS' ? 'bg-green-100 text-green-800' :
                                log.transport === 'TCP' ? 'bg-blue-100 text-blue-800' :
                                log.transport === 'UDP' ? 'bg-gray-100 text-gray-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {log.transport}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                log.direction === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                              }`}>
                                {log.direction === 'inbound' ? '-> In' : '<- Out'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {log.sipMethod ? (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                  {log.sipMethod}
                                </span>
                              ) : log.responseCode ? (
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  log.responseCode < 200 ? 'bg-gray-100 text-gray-700' :
                                  log.responseCode < 300 ? 'bg-green-100 text-green-700' :
                                  log.responseCode < 400 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {log.responseCode} {log.reasonPhrase}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 font-mono max-w-xs break-all">{log.callId}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                              {log.sipPeerId ? (
                                <div className="flex flex-col">
                                  <span className="text-blue-600">
                                    {sipPeerNameMap.get(log.sipPeerId) || `${log.sipPeerId.substring(0, 12)}...`}
                                  </span>
                                  <span className="text-xs text-gray-400">{log.sipPeerId.substring(0, 12)}...</span>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{log.containerId.substring(0, 8)}</span>
                                {(log.rawMessage || log.body) && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700" title="Has message content">
                                    {log.rawMessage ? 'RAW' : 'SDP'}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(page * pageSize, logsMeta.total)}</span> of{' '}
                    <span className="font-medium">{logsMeta.total.toLocaleString()}</span> SIP messages
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const nextPage = Math.max(1, page - 1);
                        setPage(nextPage);
                        updateUrl(filters, nextPage);
                      }}
                      disabled={page === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => {
                        const nextPage = Math.min(logsMeta.totalPages, page + 1);
                        setPage(nextPage);
                        updateUrl(filters, nextPage);
                      }}
                      disabled={page === logsMeta.totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
            </div>
          </>
        )}
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">SIP Message Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Timestamp</label>
                    <p className="text-sm text-gray-900">{formatTimestamp(selectedLog.timestamp)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Transport</label>
                    <p className="text-sm text-gray-900">{selectedLog.transport}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Direction</label>
                    <p className="text-sm text-gray-900 capitalize">{selectedLog.direction}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Container</label>
                    <p className="text-sm text-gray-900 font-mono">{selectedLog.containerId}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">SIP Peer</label>
                    <p className="text-sm text-gray-900">
                      {selectedLog.sipPeerId ? (
                        <span className="font-mono">
                          {sipPeerNameMap.get(selectedLog.sipPeerId) || selectedLog.sipPeerId}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </p>
                    {selectedLog.sipPeerId && (
                      <p className="text-xs text-gray-400 font-mono break-all">{selectedLog.sipPeerId}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-500">Call ID</label>
                    <p className="text-sm text-gray-900 font-mono break-all">{selectedLog.callId}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Method/Response</label>
                    <p className="text-sm text-gray-900">
                      {selectedLog.sipMethod || (selectedLog.responseCode ? `${selectedLog.responseCode} ${selectedLog.reasonPhrase}` : 'N/A')}
                    </p>
                  </div>
                </div>

                {/* Raw Message - Show first if available (most useful for debugging) */}
                {selectedLog.rawMessage && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Raw SIP Message
                      <span className="ml-2 text-xs font-normal text-green-600">(Complete message as sent/received)</span>
                    </label>
                    <pre className="mt-1 p-4 bg-gray-900 text-green-400 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                      {selectedLog.rawMessage}
                    </pre>
                  </div>
                )}

                {selectedLog.headers && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">SIP Headers (Parsed)</label>
                    <pre className="mt-1 p-4 bg-gray-50 rounded-lg text-xs overflow-x-auto">
                      {headerEntries(selectedLog.headers).map(([name, value], index) => (
                        <div key={`${name}-${index}`}><span className="font-semibold">{name}:</span> {value}</div>
                      ))}
                    </pre>
                  </div>
                )}

                {selectedLog.body && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">SIP Message Body (SDP)</label>
                    <pre className="mt-1 p-4 bg-gray-900 text-green-400 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                      {selectedLog.body}
                    </pre>
                  </div>
                )}

                {!selectedLog.rawMessage && !selectedLog.body && (
                  <div className="text-sm text-gray-500 italic">
                    No message body or raw message logged. Enable "Log Raw Messages" or "Log SDP Bodies" in settings to capture full message content.
                  </div>
                )}

                {/* Call Flow Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-500">
                      Call Flow
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        (All events with Call-ID: {selectedLog.callId.substring(0, 16)}...)
                      </span>
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">
                        {callFlowData ? `Page ${callFlowData.page}: ${callFlowEvents.length} of ${callFlowData.total.toLocaleString()} · frozen through ${formatTimestamp(callFlowData.traceEndTime)}` : 'Trace'}
                      </span>
                      {callFlowData?.hasMore && (
                        <button
                          type="button"
                          className="text-xs font-medium text-blue-600 hover:text-blue-700"
                          onClick={() => setTracePage((value) => value + 1)}
                        >
                          Next messages
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-xs font-medium text-blue-600 hover:text-blue-700"
                        onClick={() => { setTracePage(1); setTraceEndTime(undefined); }}
                      >
                        Refresh latest
                      </button>
                      {!callFlowData?.hasMore && callFlowData && <span className="text-xs text-gray-500">End of bounded trace</span>}
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                        onClick={handleDownloadCallFlow}
                        disabled={callFlowEvents.length === 0}
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </button>
                    </div>
                  </div>
                  {callFlowLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 text-gray-500">Loading call flow...</span>
                    </div>
                  ) : callFlowError ? (
                    <div role="alert" className="text-sm text-red-700 py-4">Call trace could not be loaded. Try again.</div>
                  ) : callFlowEvents.length > 0 ? (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <SipLadderDiagram
                        events={callFlowEvents}
                        sipPeerNameMap={sipPeerNameMap}
                        showAllDetails={false}
                        onEventClick={(event) => {
                          // Switch to the clicked event in the modal
                           openLog(event);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic py-4 text-center">
                      No other events found for this call.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
